import { useEffect, useState } from 'react';

// Which region (and so which currency) a visitor is shown, in order of trust:
//   1. their own choice in the header's region picker;
//   2. the country their connection is in, which the site's own edge (the
//      hosting network's /cdn-cgi/trace) will tell any page that asks -- no
//      third party sees the visitor;
//   3. only when that can't be had (blocked, offline, an unsupported
//      country): the device's time zone, then its main language;
//   4. the UK.
// public/static-public.js carries a vanilla copy of all of this for the
// pages that ship without React; keep the two in step (a test checks that
// they agree).

// Real, distinct IANA time zone names (not offset-based -- these city
// names are region-specific and never returned for a visitor elsewhere
// sharing the same UTC offset). Intl.DateTimeFormat always resolves to one
// of these canonical names on a modern browser; America/Montreal is kept
// too since it can still appear on an older platform even though modern tz
// data merged it into America/Toronto.
const REGION_TIME_ZONES = {
  CA: new Set([
    'America/St_Johns', 'America/Halifax', 'America/Glace_Bay', 'America/Moncton', 'America/Goose_Bay',
    'America/Toronto', 'America/Montreal', 'America/Nipigon', 'America/Thunder_Bay',
    'America/Winnipeg', 'America/Rainy_River', 'America/Regina', 'America/Swift_Current',
    'America/Edmonton', 'America/Cambridge_Bay', 'America/Yellowknife', 'America/Inuvik',
    'America/Vancouver', 'America/Whitehorse', 'America/Dawson',
    'America/Iqaluit', 'America/Rankin_Inlet', 'America/Resolute', 'America/Atikokan',
    'America/Blanc-Sablon', 'America/Creston', 'America/Dawson_Creek', 'America/Fort_Nelson'
  ]),
  AU: new Set([
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide',
    'Australia/Perth', 'Australia/Darwin', 'Australia/Hobart', 'Australia/Broken_Hill',
    'Australia/Lindeman', 'Australia/Currie', 'Australia/Eucla', 'Australia/Lord_Howe'
  ])
};

const REGION_LANGUAGE_SUFFIXES = { CA: '-ca', AU: '-au' };

// What the device itself says, for when the connection's country can't be
// had. The time zone goes first: it follows where the device is, while a
// language tag follows what someone picked (a UK visitor with English
// (Australia) somewhere in their language list is still in the UK). Only the
// main language counts, never the rest of the list, and neither signal
// returns anything unless it clearly names Canada or Australia, so a miss
// can only fail to spot a Canadian or Australian visitor, never send a UK
// one to the wrong prices.
export const detectDeviceRegion = () => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      for (const region of Object.keys(REGION_TIME_ZONES)) {
        if (REGION_TIME_ZONES[region].has(timeZone)) return region;
      }
    }

    const mainLanguage = String(
      (typeof navigator !== 'undefined' && navigator.languages && navigator.languages.length)
        ? navigator.languages[0]
        : (typeof navigator !== 'undefined' ? navigator.language : '')
    ).toLowerCase();
    for (const region of Object.keys(REGION_LANGUAGE_SUFFIXES)) {
      if (mainLanguage.endsWith(REGION_LANGUAGE_SUFFIXES[region])) return region;
    }
  } catch (error) {
    // Fall through to null below.
  }
  return null;
};

// The full catalog of regions a visitor can be shown or manually select --
// GB is both the fallback for a visitor detection can't place and a real,
// selectable choice, so every region (detected or overridden) always
// resolves to one of these three, never a blank/unknown state.
export const REGIONS = [
  { code: 'GB', label: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}', currency: 'GBP', homepageRoute: '/' },
  { code: 'CA', label: 'Canada', flag: '\u{1F1E8}\u{1F1E6}', currency: 'CAD', homepageRoute: '/cfast' },
  { code: 'AU', label: 'Australia', flag: '\u{1F1E6}\u{1F1FA}', currency: 'AUD', homepageRoute: '/adf' }
];

const getRegion = (code) => REGIONS.find((region) => region.code === code);

// The countries a connection can be in that we have a region for: the UK
// (and the Crown dependencies, which use sterling), Canada and Australia.
// Any other country -- the US, Ireland, New Zealand, the rest of the world --
// has no currency of its own on offer, so it goes on to the device signals
// and then the UK default.
const COUNTRY_REGIONS = { GB: 'GB', GG: 'GB', JE: 'GB', IM: 'GB', CA: 'CA', AU: 'AU' };

export const regionForCountry = (country) => COUNTRY_REGIONS[country] || null;

// The edge answers /cdn-cgi/trace with plain "key=value" lines, one of them
// "loc=<ISO country code>". "XX" (and "T1", Tor) mean it couldn't say, which
// is a real answer worth remembering; anything that isn't a trace at all (a
// dev server handing back the app's HTML, say) returns null, so it's never
// mistaken for one.
export const parseTraceCountry = (text) => {
  const match = /^loc=([A-Za-z0-9]{2})\s*$/m.exec(String(text || ''));
  if (!match) return null;
  const code = match[1].toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : 'XX';
};

const GEO_TRACE_URL = '/cdn-cgi/trace';
const GEO_COUNTRY_STORAGE_KEY = 'cbat_geo_country';
const GEO_TIMEOUT_MS = 1500;

// Per tab, not per device: the country is asked for once per visit, so a
// visitor who later travels or switches VPN gets the right region on their
// next visit without having to know about the picker.
let memoryCountry = null;
let lookupPromise = null;
let lookupFinished = false;
const regionListeners = new Set();

const notifyRegionChange = () => {
  regionListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      // One listener failing must not stop the others hearing about it.
    }
  });
};

export const subscribeToRegionChanges = (listener) => {
  regionListeners.add(listener);
  return () => { regionListeners.delete(listener); };
};

export const getVisitorCountry = () => {
  try {
    const stored = window.sessionStorage.getItem(GEO_COUNTRY_STORAGE_KEY);
    if (/^[A-Z]{2}$/.test(stored || '')) return stored;
  } catch (error) {
    // Blocked storage: fall back to what this page load already learned.
  }
  return memoryCountry;
};

const rememberCountry = (country) => {
  memoryCountry = country;
  try {
    window.sessionStorage.setItem(GEO_COUNTRY_STORAGE_KEY, country);
  } catch (error) {
    // The in-memory copy above still serves the rest of this page load.
  }
};

// Always settles within GEO_TIMEOUT_MS, whatever the request does (a browser
// without AbortController can't cancel one that hangs).
const fetchVisitorCountry = () => new Promise((resolve) => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    resolve(null);
    return;
  }
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => {
    if (controller) controller.abort();
    resolve(null);
  }, GEO_TIMEOUT_MS);
  const finish = (country) => {
    clearTimeout(timer);
    resolve(country);
  };
  try {
    Promise.resolve(window.fetch(GEO_TRACE_URL, {
      cache: 'no-store',
      credentials: 'omit',
      ...(controller ? { signal: controller.signal } : {})
    }))
      .then((response) => (response && response.ok ? response.text() : null))
      .then((text) => finish(text == null ? null : parseTraceCountry(text)))
      .catch(() => finish(null));
  } catch (error) {
    finish(null);
  }
});

// Asks the edge once and shares the answer with everything that's waiting.
// Never rejects: a lookup that can't be made just leaves the device signals
// to decide, and isn't remembered, so the next page tries again.
export const resolveVisitorCountry = () => {
  const known = getVisitorCountry();
  if (known) return Promise.resolve(known);
  if (!lookupPromise) {
    lookupPromise = fetchVisitorCountry().then((country) => {
      lookupFinished = true;
      if (country) rememberCountry(country);
      notifyRegionChange();
      return country;
    });
  }
  return lookupPromise;
};

// A visitor's own choice (e.g. travelling, or on a VPN that points the
// connection at the wrong country) always overrides auto-detection.
// Scoped to the browser, not the account -- this is a device preference,
// not user data, so it deliberately isn't part of storage.js's guest/account
// migration.
const REGION_OVERRIDE_STORAGE_KEY = 'cbat_region_override';

export const getRegionOverride = () => {
  try {
    const stored = window.localStorage.getItem(REGION_OVERRIDE_STORAGE_KEY);
    return getRegion(stored) ? stored : null;
  } catch (error) {
    return null;
  }
};

export const setRegionOverride = (code) => {
  try {
    if (getRegion(code)) {
      window.localStorage.setItem(REGION_OVERRIDE_STORAGE_KEY, code);
    } else if (code == null) {
      window.localStorage.removeItem(REGION_OVERRIDE_STORAGE_KEY);
    }
    // A truthy but invalid code (never expected from the UI, which only
    // ever passes a REGIONS code) is silently ignored rather than clearing
    // an existing valid override.
  } catch (error) {
    // A blocked/unavailable localStorage (private browsing, disabled site
    // data) should never throw -- the override just won't persist.
  }
  notifyRegionChange();
};

// Never null: an explicit override wins outright, then where the connection
// is, then the device's own settings, then GB -- itself a real, selectable
// region and the same fallback detectPreferredCurrency already used.
export const getEffectiveRegion = () => (
  getRegionOverride() || regionForCountry(getVisitorCountry()) || detectDeviceRegion() || 'GB'
);

export const detectPreferredCurrency = () => getRegion(getEffectiveRegion())?.currency || 'GBP';

// True once the answer can't change under a page that has already acted on
// it: the visitor chose a region, or the country lookup has come back (or
// failed). A page that fetches prices waits for this, so it never shows or
// charges in the currency of a guess it then has to take back.
export const isRegionSettled = () => Boolean(getRegionOverride()) || lookupFinished || getVisitorCountry() !== null;

const readRegionState = () => ({ region: getEffectiveRegion(), settled: isRegionSettled() });

// The live region for a component: updates when the visitor picks one, or
// when the country lookup lands, instead of being read once at mount.
export const useVisitorRegion = () => {
  const [state, setState] = useState(readRegionState);

  useEffect(() => {
    const refresh = () => setState((previous) => {
      const next = readRegionState();
      return previous.region === next.region && previous.settled === next.settled ? previous : next;
    });
    refresh();
    const unsubscribe = subscribeToRegionChanges(refresh);
    // A visitor who has picked a region has already settled it.
    if (!getRegionOverride()) void resolveVisitorCountry();
    return unsubscribe;
  }, []);

  return {
    region: state.region,
    currency: (getRegion(state.region) || REGIONS[0]).currency,
    settled: state.settled
  };
};

// Forgets what this page load has learned, for tests that need a fresh start.
export const resetRegionStateForTests = () => {
  memoryCountry = null;
  lookupPromise = null;
  lookupFinished = false;
  regionListeners.clear();
  try {
    window.sessionStorage.removeItem(GEO_COUNTRY_STORAGE_KEY);
  } catch (error) {
    // Nothing stored to forget.
  }
};
